import React, { useEffect, useCallback, useRef, useState } from "react";
import { Skeleton, Button } from "antd";
import { EditOutlined, CloseOutlined, ArrowRightOutlined, LoadingOutlined, CheckCircleFilled, HistoryOutlined } from "@ant-design/icons";
import ReviewAllChangesModal from "../components/SettingsPage/ReviewAllChangesModal";
import ConfigVersionHistoryModal from "../components/SettingsPage/ConfigVersionHistoryModal";
import GeneralLayout from "../components/GeneralLayout";
import { SettingsContainer } from "../styles/SettingsPage.styled";
import { useSettings } from "../hooks/SettingsPage/useSettings.ts";
import { fetchBrandTree } from "../api/petFormulaApi";
import { COUNTRY_CODE_TO_NAME } from "../components/MarketOverviewPage/data/constants";
import { OneFormulaTable, CoreBrandsTable, NameCompletionTable } from "../components/SettingsPage";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "@/store/types";
import { fetchCompaniesWithBrandsData } from "../slices/competitorsSlice";
import { clearAllOutletsData } from "../slices/outletSlice";
import "../components/SettingsPage/messageStyles.css";
import { useNavigationBlocking } from "../contexts/NavigationBlockingContext";
import { processApiDataToTree, getFilteredBrandOptions } from "../utils/SettingsPage/apiDataProcessing";
import { findBrandByName, mergeCompleteSubbrandsToParent, getSelectedBrandNames } from "../utils/SettingsPage/brandSelectionUtils";
import { generateCoreBrandData } from "../utils/SettingsPage/brandDataTramsformer";

const SettingsPage: React.FC = () => {
  const {
    // 🔄 Message API
    contextHolder,

    // 🔄 Loading state
    loading,
    isPipelineRunning,
    setIsPipelineRunning,
    pipelineResult,
    versionCount,

    // 🔄 Data state
    apiOneFormulaData,
    displayCoreBrandsData,

    // 🔧 Editing state
    isEditingOneFormula,
    editingOneFormulaData,
    isEditingCoreBrands,
    editingCoreBrandsData,
    editingCoreBrandsRows,

    // 🎛️ Row selection
    oneFormulaRowSelection,
    coreBrandsRowSelection,

    // 🔧 Edit-all mode
    isEditAllMode,
    setIsEditAllMode,

    // 🔧 Edit handlers
    handleEditAllOneFormula,
    handleCancelOneFormula,
    validateOneFormulaData,
    handleInputChange,
    handleApplyDefaultToEditingRow,
    handleSaveDefaultValue,
    handleApplyDefaultToAll,
    handleEditAllCoreBrands,
    handleCancelCoreBrands,
    handleCoreBrandInputChange,
    handleCoreBrandClearAndCompact,
    handleEditCoreBrandsRow,
    handleSaveCoreBrandsRow,

    // 📡 User data
    userData,

    // ========== Name Completion ==========
    ncTableData,
    ncEditingData,
    isEditingNc,
    handleEnterEditNc,
    handleCancelNc,
    handleNcInputChange,
    handleNcSaveDefaultValue,
    handleNcApplyDefaultToAll,
    handleNcApplyDefaultToRow,
    handleNcApplyDefaultToEditingRow,
    validateNcData,
  } = useSettings();

  // find the right useSelector usage
  const authIdClient = useSelector((state: any) => state.auth.id_client);
  const selectedWorkspace = useSelector((state: any) => state.auth.selectedWorkspace);
  const dispatch = useDispatch<AppDispatch>();

  // 🔧 Review modal state for edit-all mode
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  // 🕓 Version history modal state
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);

  const handleRestoreSuccess = () => {
    setIsPipelineRunning(true); // Start polling for pipeline completion
    dispatch(clearAllOutletsData());
  };

  // Auto-exit edit-all mode when all three tables finish editing (after save or cancel)
  useEffect(() => {
    if (isEditAllMode && !isEditingOneFormula && !isEditingCoreBrands && !isEditingNc) {
      setIsEditAllMode(false);
    }
  }, [isEditingOneFormula, isEditingCoreBrands, isEditingNc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enter combined edit mode for all three tables at once
  const handleEnterEditAll = () => {
    handleEditAllOneFormula(); // enters formula edit mode (toggle: not editing → editing)
    handleEditAllCoreBrands(); // enters core brands edit mode
    handleEnterEditNc(); // enters name completion edit mode
    initializeTreeSelectStatesForEditing();
    const firstRecord = displayCoreBrandsData[0];
    if (firstRecord?.market) {
      fetchBrandData(getCountryCodeFromMarket(firstRecord.market), authIdClient);
    }
    setIsEditAllMode(true);
  };

  // Discard all changes and exit combined edit mode
  const handleCancelAll = () => {
    handleCancelOneFormula();
    handleCancelCoreBrands();
    handleCancelNc();
    setTreeSelectStates({});
    setIsEditAllMode(false);
  };

  // Open review modal after validating formula and NC weights
  const handleReviewSaveAll = () => {
    if (!validateOneFormulaData()) return;
    if (isEditingNc && !validateNcData()) return;
    setIsReviewModalOpen(true);
  };

  // Confirmed save: single pipeline for ONE Formula, Core Brands, and NC weights
  const handleConfirmSaveAll = async () => {
    setIsReviewModalOpen(false);
    try {
      const ncOptions = isEditingNc ? { ncEditingData, ncTableData } : undefined;
      await handleEditAllOneFormula(ncOptions); // saves formula + core brands + NC weights in one call
      handleCancelCoreBrands();
      if (isEditingNc) handleCancelNc(); // exit NC edit mode after save
      setTreeSelectStates({});
      setIsEditAllMode(false);
      dispatch(clearAllOutletsData());
    } catch {
      // Keep edit mode active so user can retry or discard manually
    }
  };

  // 🚨 Navigation blocking context
  const { setHasUnsavedChanges, setOnDiscardChanges } = useNavigationBlocking();

  // Check if there are unsaved changes
  const hasUnsavedChanges = isEditingOneFormula || isEditingCoreBrands || isEditingNc;

  // Store handlers in refs to avoid dependency issues
  const handleCancelOneFormulaRef = useRef(handleCancelOneFormula);
  const handleCancelCoreBrandsRef = useRef(handleCancelCoreBrands);

  // Update refs when handlers change
  useEffect(() => {
    handleCancelOneFormulaRef.current = handleCancelOneFormula;
    handleCancelCoreBrandsRef.current = handleCancelCoreBrands;
  }, [handleCancelOneFormula, handleCancelCoreBrands]);

  // Register unsaved changes state with context
  // Note: setHasUnsavedChanges is stable (from useState), but we exclude it from deps to be safe
  useEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  // Register discard callback with context
  // Use useCallback to memoize the callback and refs to avoid handler dependency issues
  const discardCallback = useCallback(() => {
    // Discard changes using refs to avoid dependency on handler functions
    if (isEditingOneFormula) {
      handleCancelOneFormulaRef.current();
    }
    if (isEditingCoreBrands) {
      handleCancelCoreBrandsRef.current();
    }
    if (isEditingNc) {
      handleCancelNc();
    }
    setTreeSelectStates({});
    setIsEditAllMode(false);
  }, [isEditingOneFormula, isEditingCoreBrands, isEditingNc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOnDiscardChanges(() => discardCallback);

    return () => {
      setOnDiscardChanges(null);
    };
  }, [discardCallback]); // eslint-disable-line react-hooks/exhaustive-deps

  // Block browser navigation (back button, closing tab, etc.)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const { companiesWithBrands, loading: companiesWithBrandsLoading } =
    useSelector((state: any) => state.competitors);

  const [brandOptions, setBrandOptions] = React.useState<any[]>([]);

  // Brand data cached per countryCode, so revisiting a market skips the fetch
  const brandOptionsCache = React.useRef<Map<string, any[]>>(new Map());

  // countryCode of the market whose data should win; guards against race conditions
  const currentCountryCodeRef = React.useRef<string | null>(null);

  // In-flight requests by countryCode, so a superseded one can be aborted
  const pendingFetchRef = React.useRef<Map<string, AbortController>>(new Map());

  // countryCodes of every in-flight request; drives the brand-data loading flag
  const activeRequestsRef = React.useRef<Set<string>>(new Set());

  const [brandDataLoading, setBrandDataLoading] = React.useState(false);

  // TreeSelect popup state, keyed by `<rowKey>_coreBrand<n>`
  const [treeSelectStates, setTreeSelectStates] = React.useState<{
    [key: string]: {
      isOpen: boolean;
      searchValue: string;
      selectedBrands: string[];
      customName: string;
      tempSelectedBrands: string[];
      tempCustomName: string;
      hasError: boolean;
    };
  }>({});

  /**
   * Gets the TreeSelect state for a specific field key
   * Returns the existing state or creates a default state structure if none exists
   *
   * @param {string} fieldKey - Unique identifier for the TreeSelect field (e.g., "row1_coreBrand1")
   * @returns {object} TreeSelect state object containing isOpen, searchValue, selectedBrands, customName, etc.
   *
   * @example
   * const state = getTreeSelectState("market1_coreBrand1");
   * // Returns: { isOpen: false, searchValue: "", selectedBrands: [], ... }
   */
  const getTreeSelectState = (fieldKey: string) => {
    return (
      treeSelectStates[fieldKey] || {
        isOpen: false,
        searchValue: "",
        selectedBrands: [],
        customName: "",
        tempSelectedBrands: [],
        tempCustomName: "",
        hasError: false,
      }
    );
  };

  /**
   * Updates the TreeSelect state for a specific field key with partial updates
   * Merges the provided updates into the existing state while preserving other properties
   *
   * @param {string} fieldKey - Unique identifier for the TreeSelect field (e.g., "row1_coreBrand1")
   * @param {Partial} updates - Partial object containing the properties to update in the state
   *
   * @example
   * updateTreeSelectState("market1_coreBrand1", {
   *   isOpen: true,
   *   searchValue: "coca"
   * });
   */
  const updateTreeSelectState = useCallback((
    fieldKey: string,
    updates: Partial<(typeof treeSelectStates)[string]>
  ) => {
    setTreeSelectStates((prev) => ({
      ...prev,
      [fieldKey]: {
        ...(prev[fieldKey] || {
          isOpen: false,
          searchValue: "",
          selectedBrands: [],
          customName: "",
          tempSelectedBrands: [],
          tempCustomName: "",
          hasError: false,
        }),
        ...updates,
      },
    }));
  }, []); // setTreeSelectStates is stable (from useState)

  // Seeds TreeSelect state from saved core brand values for every row
  const initializeTreeSelectStatesForEditing = () => {
    const currentDataSource =
      displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [];

    if (currentDataSource.length > 0) {
      currentDataSource.forEach((record: any) => {
        for (let i = 1; i <= 8; i++) {
          const fieldKey = `${record.key}_coreBrand${i}`;
          const currentBrandValue =
            record[`coreBrand${i}` as keyof typeof record];

          // "-" is the empty-cell placeholder, so there is nothing to restore
          if (currentBrandValue && currentBrandValue !== "-") {
            const existingState = getTreeSelectState(fieldKey);

            // Helper: check if a string is a JSON blob (not a plain display name)
            const isJsonBlob = (val: string) => {
              try { const p = JSON.parse(val); return typeof p === 'object' && p !== null; }
              catch { return false; }
            };

            // Skip initialization only if the existing customName is a plain (non-JSON) string
            const existingCustomName = existingState.customName || "";
            const existingTempCustomName = existingState.tempCustomName || "";
            const hasPlainCustomName =
              existingCustomName.trim() !== "" && !isJsonBlob(existingCustomName);
            const hasPlainTempCustomName =
              existingTempCustomName.trim() !== "" && !isJsonBlob(existingTempCustomName);

            if (!hasPlainCustomName && !hasPlainTempCustomName) {
              try {
                const parsedData = JSON.parse(currentBrandValue);

                if (parsedData.displayName) {
                  // Only the display name is restored; the brand selection is
                  // left empty rather than re-matched against brandOptions
                  updateTreeSelectState(fieldKey, {
                    customName: parsedData.displayName,
                    tempCustomName: parsedData.displayName,
                    selectedBrands: [],
                    tempSelectedBrands: [],
                  });
                }
              } catch {
                // Non-JSON values are plain display-name strings
                updateTreeSelectState(fieldKey, {
                  customName: currentBrandValue,
                  tempCustomName: currentBrandValue,
                  selectedBrands: [],
                  tempSelectedBrands: [],
                });
              }
            }
          }
        }
      });
    }
  };

  /**
   * Initializes TreeSelect states for a specific row when editing begins
   * Parses saved core brand data for the given row and sets up UI state for its 8 core brand fields
   * Handles both JSON format (new) and string format (legacy) saved data
   * Only initializes fields that don't already have custom names set
   *
   * @param {string} rowKey - The key identifier for the specific row to initialize
   */
  const initializeTreeSelectStatesForSingleRow = (rowKey: string) => {
    const currentDataSource =
      displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [];

    const record = currentDataSource.find((item: any) => item.key === rowKey);

    if (!record) {
      console.warn(
        `[initializeTreeSelectStatesForSingleRow] Record not found for key: ${rowKey}`
      );
      return;
    }

    // Each row carries 8 Core Brand fields
    for (let i = 1; i <= 8; i++) {
      const fieldKey = `${record.key}_coreBrand${i}`;
      const currentBrandValue = record[`coreBrand${i}` as keyof typeof record];

      // "-" is the empty-cell placeholder, so there is nothing to restore
      if (currentBrandValue && currentBrandValue !== "-") {
        const existingState = getTreeSelectState(fieldKey);

        const isJsonBlob = (val: string) => {
          try { const p = JSON.parse(val); return typeof p === 'object' && p !== null; }
          catch { return false; }
        };

        // Skip initialization only if the existing customName is a plain (non-JSON) string
        const existingCustomName = existingState.customName || "";
        const existingTempCustomName = existingState.tempCustomName || "";
        const hasPlainCustomName =
          existingCustomName.trim() !== "" && !isJsonBlob(existingCustomName);
        const hasPlainTempCustomName =
          existingTempCustomName.trim() !== "" && !isJsonBlob(existingTempCustomName);

        if (!hasPlainCustomName && !hasPlainTempCustomName) {
          try {
            const parsedData = JSON.parse(currentBrandValue);

            if (parsedData.displayName) {
              // Only the display name is restored; the brand selection is
              // left empty rather than re-matched against brandOptions
              updateTreeSelectState(fieldKey, {
                customName: parsedData.displayName,
                tempCustomName: parsedData.displayName,
                selectedBrands: [],
                tempSelectedBrands: [],
              });
            }
          } catch {
            // Non-JSON values are plain display-name strings
            updateTreeSelectState(fieldKey, {
              customName: currentBrandValue,
              tempCustomName: currentBrandValue,
              selectedBrands: [],
              tempSelectedBrands: [],
            });
          }
        } else {
          console.log(
            `[initializeTreeSelectStatesForSingleRow] Skipping ${fieldKey} - already has plain values:`,
            {
              hasPlainCustomName,
              hasPlainTempCustomName,
            }
          );
        }
      }
    }
  };

  /**
   * Gets the country code corresponding to a market name
   * Performs both exact and fuzzy matching against COUNTRY_CODE_TO_NAME mapping
   * Handles cases like "US Market" -> "US" and falls back to "US" if no match found
   *
   * @param {string} marketName - The market name to convert to country code
   * @returns {string} Two-letter country code (e.g., "US", "UK", "DE")
   *
   * @example
   * getCountryCodeFromMarket("United States"); // "US"
   * getCountryCodeFromMarket("US Market"); // "US"
   * getCountryCodeFromMarket("Unknown Market"); // "US" (default)
   */
  const getCountryCodeFromMarket = (marketName: string): string => {
    // Exact match
    for (const [code, name] of Object.entries(COUNTRY_CODE_TO_NAME)) {
      if (name === marketName) {
        return code;
      }
    }

    // Fuzzy match, covering cases like "US Market" -> "US"
    const marketNameLower = marketName.toLowerCase();
    for (const [code, name] of Object.entries(COUNTRY_CODE_TO_NAME)) {
      const nameLower = (name as string).toLowerCase();
      if (
        marketNameLower.includes(nameLower) ||
        nameLower.includes(marketNameLower)
      ) {
        return code;
      }
    }

    // No match, so fall back to the default
    // Only log warning if it's actually a problem (not during normal operation)
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "[getCountryCodeFromMarket] Could not find country code for market:",
        marketName,
        "using default US"
      );
    }
    return "US";
  };

  /**
   * Fetches brand data for a specific country with intelligent caching and race condition handling
   * Uses cached data when available, cancels duplicate requests, and handles request abortion
   * Updates loading states and processes API data into TreeSelect format
   *
   * @param {string} countryCode - Two-letter country code (e.g., "US", "UK")
   * @param {string} authIdClient - Client authentication ID for API requests
   * @returns {Promise<void>} Resolves when data is fetched and processed
   *
   * @example
   * await fetchBrandData("US", "client123");
   * // Fetches US brand data, caches it, and updates UI state
   */
  const fetchBrandData = async (countryCode: string, authIdClient: string) => {
    if (brandOptionsCache.current.has(countryCode)) {
      const cachedData = brandOptionsCache.current.get(countryCode);
      if (cachedData) {
        currentCountryCodeRef.current = countryCode;
        // Reusing the cached array reference keeps downstream useMemo hooks stable
        setBrandOptions(cachedData);
        setBrandDataLoading(false);
        return;
      }
    }

    // Abort an in-flight request for the same market so results cannot interleave
    const existingController = pendingFetchRef.current.get(countryCode);
    if (existingController) {
      existingController.abort();
      pendingFetchRef.current.delete(countryCode);
      activeRequestsRef.current.delete(countryCode);
    }

    const abortController = new AbortController();
    pendingFetchRef.current.set(countryCode, abortController);
    activeRequestsRef.current.add(countryCode);

    try {
      // This request is the only one in flight, so it owns the loading flag
      if (activeRequestsRef.current.size === 1) {
        setBrandDataLoading(true);
      }
      currentCountryCodeRef.current = countryCode;

      const brandTreeData = await fetchBrandTree(
        countryCode,
        userData,
        selectedWorkspace,
        authIdClient
      );

      // The user may have switched markets while this request was outstanding
      if (abortController.signal.aborted) {
        console.log(`[fetchBrandData] Request for ${countryCode} was aborted`);
        return;
      }

      const processedBrands = processApiDataToTree(brandTreeData);

      // Only the market the user is currently on may write to brandOptions,
      // so rapid market switching cannot render a stale result
      if (
        currentCountryCodeRef.current === countryCode &&
        !abortController.signal.aborted
      ) {
        brandOptionsCache.current.set(countryCode, processedBrands);
        setBrandOptions(processedBrands);
      } else {
        console.log(
          `[fetchBrandData] Ignoring stale result for ${countryCode}, current is ${currentCountryCodeRef.current}`
        );
        // A stale result is still cached, so returning to that market is instant
        brandOptionsCache.current.set(countryCode, processedBrands);
      }
    } catch (error: any) {
      // An aborted request is an expected outcome, not a failure
      if (error.name === "AbortError") {
        console.log(`[fetchBrandData] Request for ${countryCode} was aborted`);
        return;
      }
      console.error("Error fetching brand data:", error);
    } finally {
      pendingFetchRef.current.delete(countryCode);
      activeRequestsRef.current.delete(countryCode);

      // The loading flag clears once nothing is in flight, or once the market
      // the user is on has landed. Other markets still fetching keep it on.
      if (activeRequestsRef.current.size === 0) {
        setBrandDataLoading(false);
      } else if (currentCountryCodeRef.current === countryCode) {
        setBrandDataLoading(false);
      }
    }
  };

  const fetchCompetitorsData = async (
    countryCode: string,
    authIdClient: string
  ) => {
    try {
      console.log(
        `🏆 Fetching competitors data for country: ${countryCode}, client: ${authIdClient}`
      );
      await dispatch(
        fetchCompaniesWithBrandsData({
          id_client: authIdClient,
          country: countryCode,
        })
      ).unwrap();
      console.log(`🏆 Competitors data fetched successfully`);
    } catch (error: any) {
      console.error("Error fetching competitors data:", error);
    }
  };

  /**
   * Processes competitors data from Redux store into TreeSelect format
   * Transforms company/brand hierarchical data into tree nodes for competitor selection
   * Optionally filters out a selected company to prevent self-selection
   *
   * @param {string} [selectedCompany] - Company name to exclude from the tree (optional)
   * @returns {any[]} Array of tree nodes with company names as parents and brands as children
   *
   * @example
   * const competitors = getCompetitorsTreeData("Coca Cola");
   * // Returns tree data excluding Coca Cola company, showing Pepsi, etc.
   */
  const getCompetitorsTreeData = (selectedCompany?: string) => {
    if (
      !companiesWithBrands?.companies ||
      !Array.isArray(companiesWithBrands.companies)
    ) {
      return [];
    }

    // Filter out the selected company and transform data
    const competitorOptions = companiesWithBrands.companies
      .filter((companyData: any) => companyData.company !== selectedCompany)
      .map((companyData: any) => ({
        title: companyData.company,
        value: companyData.company,
        key: companyData.company,
        children:
          companyData.brands?.map((brand: any) => ({
            title: brand.brand,
            value: `${companyData.company}|${brand.brand}`,
            key: `${companyData.company}|${brand.brand}`,
            companyName: companyData.company,
            brandName: brand.brand,
          })) || [],
      }));

    return competitorOptions;
  };

  // 🔄 Skeleton while the settings data loads
  if (loading) {
    return (
      <GeneralLayout>
        {contextHolder}
        <SettingsContainer>
          {/* Page Title Skeleton */}
          <Skeleton
            active
            title={{ width: "20%" }}
            paragraph={false}
            className="mb-6"
          />

          {/* PET Formula Table Skeleton */}
          <div className="bg-white p-6 rounded-3xl border border-[rgba(0,0,0,0.05)] shadow-[2px_4px_12px_0px_rgba(0,0,0,0.02)] mb-8">
            <Skeleton
              active
              title={{ width: "30%" }}
              paragraph={{
                rows: 8,
                width: [
                  "100%",
                  "95%",
                  "90%",
                  "100%",
                  "85%",
                  "95%",
                  "90%",
                  "100%",
                ],
              }}
            />
          </div>

          {/* Core Brands Table Skeleton */}
          <div className="bg-white p-6 rounded-3xl border border-[rgba(0,0,0,0.05)] shadow-[2px_4px_12px_0px_rgba(0,0,0,0.02)]">
            <Skeleton
              active
              title={{ width: "25%" }}
              paragraph={{
                rows: 6,
                width: ["100%", "90%", "95%", "85%", "100%", "90%"],
              }}
            />
          </div>
        </SettingsContainer>
      </GeneralLayout>
    );
  }

  return (
    <GeneralLayout>
      {contextHolder}
      <SettingsContainer style={isEditAllMode ? { paddingBottom: "80px" } : undefined}>
        {/* Page Title - sits outside the cards */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="text-[1.875rem] font-bold font-[Satoshi] text-[#000000d9]">
              Configuration
            </div>
            {isPipelineRunning && (
              <div className="flex items-center gap-1.5 text-[#8c8c8c] text-sm font-normal font-[Inter]">
                <LoadingOutlined style={{ fontSize: 14, color: "#8c8c8c" }} spin />
                <span>Updating</span>
              </div>
            )}
            {pipelineResult === "success" && (
              <div className="flex items-center gap-1.5 text-sm font-normal font-[Inter]" style={{ color: "#52c41a" }}>
                <CheckCircleFilled style={{ fontSize: 14 }} />
                <span>Update complete</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setIsVersionHistoryOpen(true)}
              style={{
                height: "2.25rem",
                padding: "0 1.25rem",
                borderRadius: "9999px",
                border: "1px solid #002882",
                fontSize: "0.875rem",
                boxShadow: "none",
                fontFamily: "Inter",
                backgroundColor: "#ffffff",
                color: "#002882",
              }}
            >
              History
            </Button>
            {!isEditingOneFormula && !isEditingCoreBrands && !isPipelineRunning && (
              <Button
                icon={<EditOutlined />}
                onClick={handleEnterEditAll}
                style={{
                  height: "2.25rem",
                  padding: "0 1.25rem",
                  borderRadius: "9999px",
                  border: "1px solid #002882",
                  fontSize: "0.875rem",
                  boxShadow: "none",
                  fontFamily: "Inter",
                  backgroundColor: "#002882",
                  color: "#ffffff",
                }}
              >
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* PET Formula Table Component */}
        <OneFormulaTable
          apiOneFormulaData={apiOneFormulaData}
          editingOneFormulaData={editingOneFormulaData}
          isEditingOneFormula={isEditingOneFormula}
          oneFormulaRowSelection={oneFormulaRowSelection}
          isPipelineRunning={isPipelineRunning}
          pipelineResult={pipelineResult}
          isEditAllMode={isEditAllMode}
          handleInputChange={handleInputChange}
          handleCancelOneFormula={handleCancelOneFormula}
          handleApplyDefaultToEditingRow={handleApplyDefaultToEditingRow}
          handleSaveDefaultValue={handleSaveDefaultValue}
          handleApplyDefaultToAll={handleApplyDefaultToAll}
        />

        {/* Core Brands Table Component */}
        <CoreBrandsTable
          displayCoreBrandsData={displayCoreBrandsData}
          editingCoreBrandsData={editingCoreBrandsData}
          isEditingCoreBrands={isEditingCoreBrands}
          editingCoreBrandsRows={editingCoreBrandsRows}
          coreBrandsRowSelection={coreBrandsRowSelection}
          isEditAllMode={isEditAllMode}
          brandOptions={brandOptions}
          brandDataLoading={brandDataLoading}
          treeSelectStates={treeSelectStates}
          handleCoreBrandInputChange={handleCoreBrandInputChange}
          handleCoreBrandClearAndCompact={handleCoreBrandClearAndCompact}
          handleEditRow={(key: string) => {
            handleEditCoreBrandsRow(key);

            // Saved values render immediately, without waiting on brand data
            initializeTreeSelectStatesForSingleRow(key);

            // Brand data is only needed once the user opens the dropdown
            const currentDataSource =
              displayCoreBrandsData.length > 0 ? displayCoreBrandsData : [];
            const record = currentDataSource.find(
              (item: any) => item.key === key
            );

            if (record && record.market) {
              const countryCode = getCountryCodeFromMarket(record.market);
              fetchBrandData(countryCode, authIdClient); // intentionally not awaited
            }
          }}
          handleSaveRow={handleSaveCoreBrandsRow}
          handleCancelCoreBrands={handleCancelCoreBrands}
          updateTreeSelectState={updateTreeSelectState}
          getTreeSelectState={getTreeSelectState}
          fetchBrandData={fetchBrandData}
          getCountryCodeFromMarket={getCountryCodeFromMarket}
          userData={userData}
          getSelectedBrandNames={(selectedValues: string[]) =>
            getSelectedBrandNames(selectedValues, brandOptions)
          }
          generateCoreBrandData={(customName: string, selectedBrands: string[]) =>
            generateCoreBrandData(customName, selectedBrands, brandOptions)
          }
          getFilteredBrandOptions={(searchValue: string) =>
            getFilteredBrandOptions(brandOptions, searchValue)
          }
          findBrandByName={findBrandByName}
          mergeCompleteSubbrandsToParent={mergeCompleteSubbrandsToParent}
          fetchCompetitorsData={fetchCompetitorsData}
          getCompetitorsTreeData={getCompetitorsTreeData}
          companiesWithBrandsLoading={companiesWithBrandsLoading}
        />

        {/* Name Completion Score Weights Table */}
        <NameCompletionTable
          tableData={ncTableData}
          editingData={ncEditingData}
          isEditing={isEditingNc}
          handleInputChange={handleNcInputChange}
          handleSaveDefaultValue={handleNcSaveDefaultValue}
          handleApplyDefaultToAll={handleNcApplyDefaultToAll}
          handleApplyDefaultToRow={handleNcApplyDefaultToRow}
          handleApplyDefaultToEditingRow={handleNcApplyDefaultToEditingRow}
        />
      </SettingsContainer>

      {/* Sticky footer — only shown in edit-all mode */}
      {isEditAllMode && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            backgroundColor: "#ffffff",
            borderTop: "1px solid #e5e7eb",
            boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
            padding: "14px 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontFamily: "Inter",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#002882",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
            <span style={{ fontSize: 14, fontWeight: 500, color: "#1f1f1f" }}>
              Editing — ONE Formula, Core Brands & Name Completion
            </span>
            <span style={{ fontSize: 13, color: "#8c8c8c" }}>
              All three tables save together with a single pipeline run
            </span>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Button
              icon={<CloseOutlined />}
              onClick={handleCancelAll}
              style={{
                height: "2rem",
                padding: "0 1rem",
                borderRadius: "9999px",
                border: "1px solid #d9d9d9",
                fontSize: "0.875rem",
                boxShadow: "none",
                fontFamily: "Inter",
              }}
            >
              Discard All
            </Button>
            <Button
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              onClick={handleReviewSaveAll}
              style={{
                height: "2rem",
                padding: "0 1.25rem",
                borderRadius: "9999px",
                border: "1px solid #002882",
                fontSize: "0.875rem",
                boxShadow: "none",
                fontFamily: "Inter",
                backgroundColor: "#002882",
                color: "#ffffff",
              }}
            >
              Review & Save All
            </Button>
          </div>
        </div>
      )}

      {/* Review all changes modal */}
      <ReviewAllChangesModal
        open={isReviewModalOpen}
        onCancel={() => setIsReviewModalOpen(false)}
        onConfirm={handleConfirmSaveAll}
        apiOneFormulaData={apiOneFormulaData}
        editingOneFormulaData={editingOneFormulaData}
        displayCoreBrandsData={displayCoreBrandsData}
        editingCoreBrandsData={editingCoreBrandsData}
        ncTableData={ncTableData}
        ncEditingData={ncEditingData}
        versionCount={versionCount}
      />

      {/* Config version history modal */}
      <ConfigVersionHistoryModal
        open={isVersionHistoryOpen}
        onClose={() => setIsVersionHistoryOpen(false)}
        onRestoreSuccess={handleRestoreSuccess}
        userData={userData}
        selectedWorkspace={selectedWorkspace}
        authIdClient={authIdClient}
        isPipelineRunning={isPipelineRunning}
      />
    </GeneralLayout>
  );
};

export default SettingsPage;
